<template>
  <div class="container">
    <h4 class="is-size-6-mobile is-size-5-tablet">
      {{ $t("transitTimeReport.page_title") }}
    </h4>

    <div class="box">
      <div class="columns is-multiline is-mobile">
        <div class="column is-12-mobile is-6-tablet is-3-desktop">
          <div class="control">
            <label class="label">{{ $t("transitTimeReport.from_date") }}:</label>
            <date-picker
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              v-model="from"
              :disabled-date="disabledAfterToday"
              :clearable="false"
              name="from"
              class="is-fullwidth"
            ></date-picker>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-3-desktop">
          <div class="control">
            <label class="label">{{ $t("transitTimeReport.to_date") }}:</label>
            <date-picker
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              v-model="to"
              :disabled-date="disabledAfterToday"
              :clearable="false"
              name="to"
              class="is-fullwidth"
            ></date-picker>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-3-desktop">
          <label class="label is-invisible">{{ $t("transitTimeReport.search") }}</label>
          <div class="buttons is-centered-mobile">
            <button
              class="button is-rounded is-fullwidth-mobile"
              @click="fetchData()"
            >
              <span class="icon"><i class="fal fa-search"></i></span>
              <span>{{ $t("transitTimeReport.search") }}</span>
            </button>
            <button
              class="button is-primary is-rounded is-fullwidth-mobile"
              @click="exportExcel()" :disabled="!report || !report.legs_available"
            >
              <span class="icon"><i class="fal fa-file-excel"></i></span>
              <span>{{ $t("transitTimeReport.export_excel") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="loading" />

    <div class="notification is-warning is-light" v-if="report && !loading && !report.legs_available">
      {{ $t("transitTimeReport.legs_unavailable") }}
    </div>

    <template v-if="report && !loading && report.legs_available">
      <!-- Window totals (freight / goods value / boxes) + the snapshot time every chart on this page shares -->
      <div class="box totals-box">
        <p class="has-text-weight-bold">
          {{ $t("transitTimeReport.totals_freight") }}: {{ formatMoney(report.totals.total_freight) }}
          &nbsp;|&nbsp;
          {{ $t("transitTimeReport.totals_price") }}: {{ formatMoney(report.totals.total_price) }}
          &nbsp;|&nbsp;
          {{ $t("transitTimeReport.totals_pieces") }}: {{ formatNumber(report.totals.pieces) }} {{ $t("transitTimeReport.box_unit") }}
        </p>
        <p class="is-size-7 has-text-grey" v-if="report.legs_as_of">
          {{ $t("transitTimeReport.legs_as_of") }}: {{ formatDateTime(report.legs_as_of) }}
        </p>
        <p class="is-size-7 has-text-danger" v-if="legsPartialCoverage">
          {{ $t("transitTimeReport.legs_partial") }}: {{ formatDate(report.legs_coverage.from) }} – {{ formatDate(report.legs_coverage.to) }}
        </p>
        <p class="is-size-7 has-text-grey" v-if="report.totals.unmapped_count">
          {{ $t("transitTimeReport.legs_unmapped") }}: {{ formatNumber(report.totals.unmapped_count) }}
        </p>
      </div>

      <!-- Headline: avg days per province, one bar per month -->
      <div class="box">
        <h5 class="is-size-6">{{ $t("transitTimeReport.overview_title") }}</h5>
        <p class="is-size-7 has-text-grey">{{ $t("transitTimeReport.select_province_hint") }}</p>
        <div class="chart-scroll">
          <div class="chart-wrapper" :style="chartStyle(provinceLabels, provinceDatasets, 420, 900)">
            <grouped-bar-chart
              :labels="provinceLabels"
              :datasets="provinceDatasets"
              :tooltip-notes="provinceTooltipNotes"
              :unit="$t('transitTimeReport.days_unit')"
              :clickable="true"
              @bar-click="onProvinceBarClick"
            />
          </div>
        </div>
      </div>

      <!-- Province overview table (reliable click fallback) -->
      <div class="box">
        <div class="table-container">
          <table class="table is-bordered is-striped is-fullwidth">
            <thead>
              <tr>
                <th rowspan="2" class="has-text-centered is-vcentered">
                  {{ $t("transitTimeReport.province") }}
                </th>
                <th
                  v-for="month in report.months"
                  :key="'h-' + month"
                  colspan="4"
                  class="has-text-centered"
                >
                  {{ monthLabel(month) }}
                </th>
                <th rowspan="2" class="has-text-centered is-vcentered"></th>
              </tr>
              <tr>
                <template v-for="month in report.months">
                  <th :key="month + '-c'" class="has-text-centered">
                    {{ $t("transitTimeReport.created_count") }}
                  </th>
                  <th :key="month + '-a'" class="has-text-centered">
                    {{ $t("transitTimeReport.arrived_count") }}
                  </th>
                  <th :key="month + '-r'" class="has-text-centered">
                    {{ $t("transitTimeReport.arrival_rate") }}
                  </th>
                  <th :key="month + '-d'" class="has-text-centered">
                    {{ $t("transitTimeReport.avg_days") }}
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="province in report.provinces"
                :key="province.province_id"
                :class="{ 'is-selected-row': province.province_id === selectedProvinceId }"
              >
                <td>
                  <strong>{{ province.name }}</strong>
                </td>
                <template v-for="(stat, i) in province.monthly">
                  <td :key="i + '-c'" class="has-text-right">{{ formatNumber(stat.created_count) }}</td>
                  <td :key="i + '-a'" class="has-text-right">{{ formatNumber(stat.arrived_count) }}</td>
                  <td :key="i + '-r'" class="has-text-right">{{ formatStat(stat.arrival_rate) }}</td>
                  <td :key="i + '-d'" class="has-text-right">
                    <strong>{{ formatStat(stat.avg_days) }}</strong>
                  </td>
                </template>
                <td class="has-text-centered">
                  <button class="button is-small is-rounded" @click="selectProvince(province.province_id)">
                    {{ $t("transitTimeReport.view_detail") }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Drill-down: one province's daily trend + districts + daily table -->
      <div class="box" v-if="selectedProvince" ref="drill">
        <div class="level is-mobile drill-header">
          <div class="level-left">
            <div>
              <h5 class="is-size-6">
                {{ selectedProvince.name }}
              </h5>
              <p class="is-size-7" v-if="report.legs_available">
                {{ $t("transitTimeReport.totals_freight") }}: {{ formatMoney(selectedProvince.totals.total_freight) }}
                &nbsp;|&nbsp;
                {{ $t("transitTimeReport.totals_price") }}: {{ formatMoney(selectedProvince.totals.total_price) }}
                &nbsp;|&nbsp;
                {{ $t("transitTimeReport.totals_pieces") }}: {{ formatNumber(selectedProvince.totals.pieces) }} {{ $t("transitTimeReport.box_unit") }}
              </p>
            </div>
          </div>
          <div class="level-right">
            <button class="button is-small is-rounded" @click="closeDrill()">
              <span class="icon"><i class="fal fa-times"></i></span>
              <span>{{ $t("transitTimeReport.back_to_overview") }}</span>
            </button>
          </div>
        </div>

        <h6 class="is-size-7 has-text-weight-bold">
          {{ $t("transitTimeReport.daily_trend_title") }}
        </h6>
        <div class="chart-scroll">
          <div class="chart-wrapper" :style="chartStyle(dailyLabels, [], 320, 700, 12)">
            <trend-line-chart
              :labels="dailyLabels"
              :values="dailyValues"
              :label="$t('transitTimeReport.average_line_label')"
              color="#E11F26"
              :unit="$t('transitTimeReport.days_unit')"
              :secondary="{label: $t('transitTimeReport.arrival_line_label'), values: dailyRates, color: '#2EA8D9', unit: '%', max: 100}"
            />
          </div>
        </div>
        <p class="is-size-7 has-text-grey">{{ $t("transitTimeReport.recent_days_note") }}</p>

        <!-- Per-leg averages: origin → DC → destination → sign, one bar per month -->
        <h6 class="is-size-7 has-text-weight-bold district-title">
          {{ $t("transitTimeReport.legs_title") }}
        </h6>
        <template v-if="report.legs_available">
          <div class="chart-scroll">
            <div class="chart-wrapper" :style="chartStyle(legLabels, legDatasets, 360, 760, 0)">
              <grouped-bar-chart
                :labels="legLabels"
                :datasets="legDatasets"
                :tooltip-notes="legTooltipNotes"
                :unit="$t('transitTimeReport.days_unit')"
              />
            </div>
          </div>
          <p class="is-size-7 has-text-grey">{{ $t("transitTimeReport.legs_note") }}</p>
        </template>
        <p class="is-size-7 has-text-grey" v-else>{{ $t("transitTimeReport.legs_unavailable") }}</p>

        <template v-if="capitalSplit">
          <h6 class="is-size-7 has-text-weight-bold district-title">
            {{ $t("transitTimeReport.capital_vs_rest_title") }}
          </h6>
          <div class="chart-scroll">
            <div class="chart-wrapper chart-wrapper--split" :style="chartStyle(capitalSplitLabels, capitalSplitDatasets, 300, 420)">
              <grouped-bar-chart
                :labels="capitalSplitLabels"
                :datasets="capitalSplitDatasets"
                :unit="$t('transitTimeReport.days_unit')"
              />
            </div>
          </div>

          <template v-if="report.legs_available">
            <h6 class="is-size-7 has-text-weight-bold district-title">
              {{ $t("transitTimeReport.capital_vs_rest_legs_title") }}
            </h6>
            <div class="chart-scroll">
              <div class="chart-wrapper" :style="chartStyle(legLabels, capitalLegDatasets, 340, 760, 0)">
                <grouped-bar-chart
                  :labels="legLabels"
                  :datasets="capitalLegDatasets"
                  :tooltip-notes="capitalLegTooltipNotes"
                  :unit="$t('transitTimeReport.days_unit')"
                />
              </div>
            </div>
          </template>
        </template>

        <h6 class="is-size-7 has-text-weight-bold district-title">
          {{ $t("transitTimeReport.district_breakdown_title") }}
        </h6>
        <div class="chart-scroll">
          <div class="chart-wrapper" :style="chartStyle(districtLabels, districtDatasets, 380, 700)">
            <grouped-bar-chart
              :labels="districtLabels"
              :datasets="districtDatasets"
              :unit="$t('transitTimeReport.days_unit')"
            />
          </div>
        </div>

        <template v-if="report.legs_available">
          <div class="level is-mobile district-title">
            <div class="level-left">
              <h6 class="is-size-7 has-text-weight-bold">
                {{ $t("transitTimeReport.district_legs_title") }}
              </h6>
            </div>
            <div class="level-right">
              <div class="select is-small">
                <select v-model="legMonthIndex">
                  <option v-for="(month, i) in report.months" :key="month" :value="i">
                    {{ monthLabel(month) }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="table-container">
            <table class="table is-bordered is-striped is-fullwidth">
              <thead>
                <tr>
                  <th class="has-text-centered">{{ $t("transitTimeReport.district") }}</th>
                  <th class="has-text-centered">{{ $t("transitTimeReport.created_count") }}</th>
                  <th class="has-text-centered">{{ $t("transitTimeReport.via_dc_count") }}</th>
                  <th v-for="key in report.leg_keys" :key="'lh-' + key" class="has-text-centered">
                    {{ legName(key) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in districtLegRows" :key="row.key" :class="{ 'has-text-weight-bold': row.isProvince }">
                  <td>{{ row.name }}</td>
                  <td class="has-text-right">{{ formatNumber(row.stat.created_count) }}</td>
                  <td class="has-text-right">{{ formatNumber(row.stat.via_dc_count) }}</td>
                  <td
                    v-for="key in report.leg_keys"
                    :key="row.key + '-' + key"
                    class="has-text-right"
                    :title="$t('transitTimeReport.leg_count') + ': ' + formatNumber(row.stat.legs[key].count)"
                  >
                    {{ formatStat(row.stat.legs[key].avg_days) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <h6 class="is-size-7 has-text-weight-bold district-title">
          {{ $t("transitTimeReport.daily_table_title") }}
        </h6>
        <div class="table-container daily-table">
          <table class="table is-bordered is-striped is-fullwidth">
            <thead>
              <tr>
                <th class="has-text-centered">{{ $t("transitTimeReport.date") }}</th>
                <th class="has-text-centered">{{ $t("transitTimeReport.created_count") }}</th>
                <th class="has-text-centered">{{ $t("transitTimeReport.arrived_count") }}</th>
                <th class="has-text-centered">{{ $t("transitTimeReport.arrival_rate") }}</th>
                <th class="has-text-centered">{{ $t("transitTimeReport.avg_days") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="day in selectedProvince.daily" :key="day.date">
                <td>{{ formatDate(day.date) }}</td>
                <td class="has-text-right">{{ formatNumber(day.created_count) }}</td>
                <td class="has-text-right">{{ formatNumber(day.arrived_count) }}</td>
                <td class="has-text-right">{{ formatStat(day.arrival_rate) }}</td>
                <td class="has-text-right"><strong>{{ formatStat(day.avg_days) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import DatePicker from "vue2-datepicker";
import Loading from "@/views/Loading";
import XLSX from "xlsx";
import GroupedBarChart from "@/components/Chart/GroupedBarChart";
import TrendLineChart from "@/components/Chart/TrendLineChart";
import {fetchTransitTimeReport} from "@/services/transitTimeReportService";

const MONTH_COLORS = ["#2EA8D9", "#E11F26", "#F6CA07", "#22c55e", "#8b5cf6", "#f97316"];

export default {
  name: "transitTimeReport",
  data() {
    return {
      // Mirrors the backend default: start of the month two months back → today
      // (= the owner's June-2026 comparison window when this shipped, and it
      // keeps rolling instead of outgrowing the backend's max-range cap).
      from: moment(new Date()).startOf("month").subtract(2, "months").format("YYYY-MM-DD"),
      to: moment(new Date()).format("YYYY-MM-DD"),
      report: null,
      selectedProvinceId: null,
      // Month shown in the district × leg table (index into report.months; set on load).
      legMonthIndex: 0,
      loading: false,
      requestSeq: 0,
    };
  },
  computed: {
    provinceLabels() {
      return this.report ? this.report.provinces.map((p) => p.name) : [];
    },
    provinceDatasets() {
      if (!this.report) return [];
      return this.report.months.map((month, i) => ({
        label: this.monthLabel(month),
        color: MONTH_COLORS[i % MONTH_COLORS.length],
        values: this.report.provinces.map((p) => p.monthly[i].avg_days),
      }));
    },
    provinceTooltipNotes() {
      if (!this.report) return null;
      const rateLabel = this.$t("transitTimeReport.arrival_rate");
      return this.report.months.map((month, i) =>
        this.report.provinces.map((p) => {
          const stat = p.monthly[i];
          if (stat.arrival_rate === null || stat.arrival_rate === undefined) return null;
          return rateLabel + ": " + stat.arrival_rate + "%";
        })
      );
    },
    // The leg table is built per creation day; a window that starts before the
    // backfill (or straddles a hole) has totals that cover only part of it.
    legsPartialCoverage() {
      const r = this.report;
      if (!r || !r.legs_available || !r.legs_coverage) return false;
      return r.legs_coverage.from > r.from || r.legs_coverage.to < r.to;
    },
    selectedProvince() {
      if (!this.report || this.selectedProvinceId === null) return null;
      return (
        this.report.provinces.find((p) => p.province_id === this.selectedProvinceId) || null
      );
    },
    dailyLabels() {
      return this.selectedProvince
        ? this.selectedProvince.daily.map((d) => this.formatDate(d.date))
        : [];
    },
    dailyValues() {
      return this.selectedProvince
        ? this.selectedProvince.daily.map((d) => d.avg_days)
        : [];
    },
    dailyRates() {
      return this.selectedProvince
        ? this.selectedProvince.daily.map((d) => d.arrival_rate)
        : [];
    },
    // Name the DC in the leg labels when one DC ships ≥ 90% of the province's
    // bills; otherwise the generic ສູນຄັດແຍກ.
    dcLabel() {
      const province = this.selectedProvince;
      const top = province && province.dcs && province.dcs.length ? province.dcs[0] : null;
      return top && top.share >= 90 ? top.name : this.$t("transitTimeReport.sorting_center");
    },
    // Per leg, one bar pair per month: ເມືອງເອກ in the month's colour, the
    // other districts in the same colour at half opacity — so the chart
    // compares months AND capital-vs-rest at once, like the other charts.
    capitalLegSides() {
      if (!this.capitalSplit || !this.report.legs_available) return [];
      const capitalName = this.capitalSplit.capital.name + " (" + this.$t("transitTimeReport.capital_district") + ")";
      const sides = [];
      this.report.months.forEach((month, i) => {
        const color = MONTH_COLORS[i % MONTH_COLORS.length];
        sides.push({side: this.capitalSplit.capital, i, label: this.monthLabel(month) + " " + capitalName, color});
        sides.push({
          side: this.capitalSplit.others,
          i,
          label: this.monthLabel(month) + " " + this.$t("transitTimeReport.other_districts"),
          color: color + "80",
        });
      });
      return sides;
    },
    capitalLegDatasets() {
      return this.capitalLegSides.map((entry) => ({
        label: entry.label,
        color: entry.color,
        values: this.visibleLegKeys.map((key) => entry.side.legs_monthly[entry.i].legs[key].avg_days),
      }));
    },
    capitalLegTooltipNotes() {
      if (!this.capitalLegSides.length) return null;
      const countLabel = this.$t("transitTimeReport.leg_count");
      return this.capitalLegSides.map((entry) =>
        this.visibleLegKeys.map(
          (key) => countLabel + ": " + this.formatNumber(entry.side.legs_monthly[entry.i].legs[key].count)
        )
      );
    },
    capitalSplit() {
      const province = this.selectedProvince;
      // Provinces without an is_capital district (10/19/20/21) have capital:null
      // — nothing to compare, hide the chart.
      return province && province.capital_split && province.capital_split.capital
        ? province.capital_split
        : null;
    },
    capitalSplitLabels() {
      if (!this.capitalSplit) return [];
      return [
        this.capitalSplit.capital.name + " (" + this.$t("transitTimeReport.capital_district") + ")",
        this.$t("transitTimeReport.other_districts"),
      ];
    },
    capitalSplitDatasets() {
      if (!this.capitalSplit) return [];
      return this.report.months.map((month, i) => ({
        label: this.monthLabel(month),
        color: MONTH_COLORS[i % MONTH_COLORS.length],
        values: [
          this.capitalSplit.capital.monthly[i].avg_days,
          this.capitalSplit.others.monthly[i].avg_days,
        ],
      }));
    },
    districtLabels() {
      if (!this.selectedProvince) return [];
      return this.selectedProvince.districts.map((d) =>
        d.is_capital ? d.name + " (" + this.$t("transitTimeReport.capital_district") + ")" : d.name
      );
    },
    districtDatasets() {
      if (!this.selectedProvince) return [];
      return this.report.months.map((month, i) => ({
        label: this.monthLabel(month),
        color: MONTH_COLORS[i % MONTH_COLORS.length],
        values: this.selectedProvince.districts.map((d) => d.monthly[i].avg_days),
      }));
    },
    // Legs: hide the direct (no-DC) leg when nothing in the window used it.
    visibleLegKeys() {
      if (!this.report || !this.selectedProvince) return [];
      return this.report.leg_keys.filter(
        (key) =>
          key !== "direct_to_dest" ||
          this.selectedProvince.legs_monthly.some((m) => m.legs[key].count > 0)
      );
    },
    legLabels() {
      return this.visibleLegKeys.map((key) => this.legName(key));
    },
    legDatasets() {
      if (!this.selectedProvince) return [];
      return this.report.months.map((month, i) => ({
        label: this.monthLabel(month),
        color: MONTH_COLORS[i % MONTH_COLORS.length],
        values: this.visibleLegKeys.map(
          (key) => this.selectedProvince.legs_monthly[i].legs[key].avg_days
        ),
      }));
    },
    legTooltipNotes() {
      if (!this.selectedProvince) return null;
      const countLabel = this.$t("transitTimeReport.leg_count");
      return this.report.months.map((month, i) =>
        this.visibleLegKeys.map(
          (key) =>
            countLabel + ": " + this.formatNumber(this.selectedProvince.legs_monthly[i].legs[key].count)
        )
      );
    },
    resolvedLegMonthIndex() {
      if (!this.report || !this.report.months.length) return 0;
      return Math.min(Math.max(this.legMonthIndex, 0), this.report.months.length - 1);
    },
    districtLegRows() {
      const province = this.selectedProvince;
      if (!province || !this.report.legs_available) return [];
      const i = this.resolvedLegMonthIndex;
      const capitalTag = " (" + this.$t("transitTimeReport.capital_district") + ")";
      return [
        {key: "province", isProvince: true, name: province.name, stat: province.legs_monthly[i]},
      ].concat(
        province.districts.map((d) => ({
          key: "d-" + d.district_id,
          isProvince: false,
          name: d.is_capital ? d.name + capitalTag : d.name,
          stat: d.legs_monthly[i],
        }))
      );
    },
  },
  methods: {
    /**
     * Size a chart wrapper from its content so nothing is clipped: width grows
     * with (groups × series) so bars never squeeze into slivers, and height adds
     * room for the rotated x-axis labels (long Lao district names) plus the
     * legend, so the plot area itself keeps at least `baseHeight`.
     */
    chartStyle(labels, datasets, baseHeight, baseMinWidth, perGroupPx) {
      const groups = labels ? labels.length : 0;
      const series = datasets && datasets.length ? datasets.length : 1;
      const groupWidth = perGroupPx || Math.max(26 * series + 22, 56);
      const longestLabel = (labels || []).reduce((max, l) => Math.max(max, String(l).length), 0);
      // 45°-rotated labels need ~5px of height per character, capped so a very
      // long name cannot push the chart off-screen.
      const labelRoom = Math.min(Math.round(longestLabel * 5), 160);
      return {
        height: baseHeight + labelRoom + "px",
        minWidth: Math.max(baseMinWidth, groups * groupWidth + 140) + "px",
      };
    },
    disabledAfterToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date > today;
    },
    monthLabel(month) {
      // '2026-06' -> '06/2026'
      const parts = month.split("-");
      return parts[1] + "/" + parts[0];
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatStat(value) {
      if (value === null || value === undefined) return "-";
      return value.toString();
    },
    formatMoney(value) {
      // Same look as the shipment list summary: whole kip, comma-grouped, with the unit.
      return this.formatNumber(Math.round(value || 0)) + " " + this.$t("transitTimeReport.kip_unit");
    },
    formatDateTime(value) {
      return moment(value).format("DD/MM/YYYY HH:mm");
    },
    legName(key) {
      return this.$t("transitTimeReport.leg_" + key, {dc: this.dcLabel});
    },
    legNameGeneric(key) {
      return this.$t("transitTimeReport.leg_" + key, {dc: this.$t("transitTimeReport.sorting_center")});
    },
    fetchData() {
      // Sequence guard: a slow earlier response must never clobber a newer one.
      const seq = ++this.requestSeq;
      this.loading = true;
      fetchTransitTimeReport({from: this.from, to: this.to})
        .then((data) => {
          if (seq !== this.requestSeq) return;
          this.report = data;
          // The district × leg table defaults to the newest month of the loaded
          // window (a stale index from a previous window would point past the end).
          this.legMonthIndex = Math.max(0, data.months.length - 1);
          // Keep the drill open across refetches only if the province still exists.
          if (
            this.selectedProvinceId !== null &&
            !data.provinces.some((p) => p.province_id === this.selectedProvinceId)
          ) {
            this.selectedProvinceId = null;
          }
          this.loading = false;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          console.log(error);
          this.$vs.notification({
            color: "danger",
            title: this.$t("transitTimeReport.error_loading"),
          });
          this.loading = false;
        });
    },
    onProvinceBarClick({index}) {
      const province = this.report && this.report.provinces[index];
      if (province) {
        this.selectProvince(province.province_id);
      }
    },
    selectProvince(provinceId) {
      this.selectedProvinceId = provinceId;
      this.$nextTick(() => {
        if (this.$refs.drill) {
          this.$refs.drill.scrollIntoView({behavior: "smooth", block: "start"});
        }
      });
    },
    closeDrill() {
      this.selectedProvinceId = null;
    },
    exportExcel() {
      if (!this.report) return;
      const t = (key) => this.$t("transitTimeReport." + key);
      const wb = XLSX.utils.book_new();

      // Sheet 1: what this workbook is — window, leg-data coverage/as-of and the
      // unmapped count, so partial totals can never be read as complete ones.
      const info = [
        {[t("info_key")]: t("from_date"), [t("info_value")]: this.formatDate(this.report.from)},
        {[t("info_key")]: t("to_date"), [t("info_value")]: this.formatDate(this.report.to)},
        {[t("info_key")]: t("legs_available_label"), [t("info_value")]: this.report.legs_available ? t("yes") : t("no")},
      ];
      if (this.report.legs_coverage) {
        info.push({
          [t("info_key")]: t("legs_coverage_label"),
          [t("info_value")]: this.formatDate(this.report.legs_coverage.from) + " - " + this.formatDate(this.report.legs_coverage.to),
        });
      }
      if (this.report.legs_as_of) {
        info.push({[t("info_key")]: t("legs_as_of"), [t("info_value")]: this.formatDateTime(this.report.legs_as_of)});
      }
      if (this.legsPartialCoverage) {
        info.push({[t("info_key")]: t("warning"), [t("info_value")]: t("legs_partial")});
      }
      info.push({[t("info_key")]: t("legs_unmapped"), [t("info_value")]: this.report.totals.unmapped_count || 0});
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(info), "Info");

      const provinceRows = [];
      this.report.provinces.forEach((province) => {
        province.monthly.forEach((stat) => {
          provinceRows.push({
            [t("province")]: province.name,
            [t("month")]: this.monthLabel(stat.month),
            [t("created_count")]: stat.created_count,
            [t("arrived_count")]: stat.arrived_count,
            [t("arrival_rate")]: stat.arrival_rate,
            [t("avg_days")]: stat.avg_days,
          });
        });
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(provinceRows), "Provinces");

      if (this.report.legs_available) {
        // All-province sheet: generic leg headers (a DC name only makes sense per
        // province) + the province's main serving DC as its own column.
        const legRows = [];
        this.report.provinces.forEach((province) => {
          const mainDc = province.dcs && province.dcs.length ? province.dcs[0] : null;
          province.legs_monthly.forEach((stat) => {
            const row = {
              [t("province")]: province.name,
              [t("main_dc")]: mainDc ? mainDc.name + " (" + mainDc.share + "%)" : "-",
              [t("month")]: this.monthLabel(stat.month),
              [t("created_count")]: stat.created_count,
              [t("via_dc_count")]: stat.via_dc_count,
            };
            this.report.leg_keys.forEach((key) => {
              row[this.legNameGeneric(key)] = stat.legs[key].avg_days;
              row[this.legNameGeneric(key) + " - " + t("leg_count")] = stat.legs[key].count;
            });
            legRows.push(row);
          });
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(legRows), "Legs");

        const totalRows = this.report.provinces.map((province) => ({
          [t("province")]: province.name,
          [t("created_count")]: province.totals.created_count,
          [t("totals_pieces")]: province.totals.pieces,
          [t("totals_freight")]: province.totals.total_freight,
          [t("totals_price")]: province.totals.total_price,
        }));
        totalRows.push({
          [t("province")]: t("legs_unmapped"),
          [t("created_count")]: this.report.totals.unmapped_count || 0,
        });
        totalRows.push({
          [t("province")]: t("all_provinces"),
          [t("created_count")]: this.report.totals.created_count,
          [t("totals_pieces")]: this.report.totals.pieces,
          [t("totals_freight")]: this.report.totals.total_freight,
          [t("totals_price")]: this.report.totals.total_price,
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(totalRows), "Totals");
      }

      if (this.selectedProvince) {
        const dailyRows = this.selectedProvince.daily.map((day) => ({
          [t("date")]: day.date,
          [t("created_count")]: day.created_count,
          [t("arrived_count")]: day.arrived_count,
          [t("arrival_rate")]: day.arrival_rate,
          [t("avg_days")]: day.avg_days,
        }));
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(dailyRows),
          ("Daily-" + this.selectedProvince.name).slice(0, 31)
        );

        const districtRows = [];
        this.selectedProvince.districts.forEach((district) => {
          district.monthly.forEach((stat) => {
            districtRows.push({
              [t("district")]: district.name,
              [t("capital_district")]: district.is_capital ? "✓" : "",
              [t("month")]: this.monthLabel(stat.month),
              [t("created_count")]: stat.created_count,
              [t("arrived_count")]: stat.arrived_count,
              [t("arrival_rate")]: stat.arrival_rate,
              [t("avg_days")]: stat.avg_days,
            });
          });
        });
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(districtRows),
          ("Districts-" + this.selectedProvince.name).slice(0, 31)
        );

        if (this.report.legs_available) {
          const districtLegRows = [];
          this.selectedProvince.districts.forEach((district) => {
            district.legs_monthly.forEach((stat) => {
              const row = {
                [t("district")]: district.name,
                [t("capital_district")]: district.is_capital ? "✓" : "",
                [t("month")]: this.monthLabel(stat.month),
                [t("created_count")]: stat.created_count,
                [t("via_dc_count")]: stat.via_dc_count,
              };
              this.report.leg_keys.forEach((key) => {
                row[this.legName(key)] = stat.legs[key].avg_days;
                row[this.legName(key) + " - " + t("leg_count")] = stat.legs[key].count;
              });
              districtLegRows.push(row);
            });
          });
          XLSX.utils.book_append_sheet(
            wb,
            XLSX.utils.json_to_sheet(districtLegRows),
            ("Legs-" + this.selectedProvince.name).slice(0, 31)
          );
        }
      }

      // Name the file after the LOADED report's window (echoed by the API), not
      // the pickers — after a failed/pending search they can differ, and the old
      // data must never go out labeled with the new range.
      XLSX.writeFile(
        wb,
        "transit-time-report " +
          moment(this.report.from).format("DD-MM-YYYY") +
          " " + this.$t("transitTimeReport.to_date_joiner") + " " +
          moment(this.report.to).format("DD-MM-YYYY") +
          ".xlsx"
      );
    },
  },
  mounted() {
    this.fetchData();
  },
  components: {
    DatePicker,
    Loading,
    GroupedBarChart,
    TrendLineChart,
  },
};
</script>

<style scoped lang="scss">
.chart-scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px 0;
}

.chart-wrapper {
  // height / min-width come from chartStyle(): the plot grows with the number of
  // bars and the length of the rotated axis labels instead of being clipped
  // inside a fixed-height card.
  position: relative;
  // vue-chartjs renders <div><canvas width=400 height=400> with NO height on its
  // div, so Chart.js (responsive, maintainAspectRatio: false) keeps the 400px
  // canvas default instead of this wrapper's height. A shorter wrapper then
  // hides the x-axis labels behind a vertical scrollbar. Hand the chart root the
  // wrapper's height so the plot always fits exactly.
  > div {
    height: 100%;
  }

  &--split {
    max-width: 640px;
  }
}

.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px 0;
}

.table {
  th,
  td {
    white-space: nowrap;
    font-size: 0.85rem;
    padding: 0.4em 0.6em;
  }

  thead th {
    background-color: #f5f5f5;
    position: sticky;
    top: 0;
  }
}

.is-selected-row {
  background-color: #fff8e6;
}

.daily-table {
  max-height: 420px;
  overflow-y: auto;
}

.drill-header {
  margin-bottom: 0.5rem;
}

.totals-box {
  padding: 0.75rem 1.25rem;
}

.district-title {
  margin-top: 1rem;
}

.box {
  @media (max-width: 768px) {
    padding: 1rem;
  }
}

.buttons {
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;

    .button {
      margin-bottom: 0.5rem;
      width: 100%;
    }
  }

  @media (min-width: 769px) {
    .button {
      margin-right: 0.5rem;
    }
  }
}

::v-deep .mx-datepicker {
  width: 100%;

  .mx-input {
    width: 100%;
  }
}
</style>
